<script lang="ts" generics="TValues, TValue extends unknown">
    import { getContainer } from '$components/Context/ContainerContext.js';
    import { MentionsQueryDocument, ParticipantAvatarFragmentDoc } from '$gql/graphql.js';
    import { clickOutside } from '$lib/utils/interractions';
    import debounce from 'lodash/debounce';
    import find from 'lodash/find';
    import { onMount, tick } from 'svelte';
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import { cx } from '$lib/utils/cx.js';
    import ParticipantAvatar from '$components/ParticipantAvatar/ParticipantAvatar.svelte';
    import CountryPicture from '$components/CountryPicture.svelte';
    import { type DocumentType, useFragment } from '$gql';

    //https://podio.github.io/jquery-mentions-input/

    export let form: Form<TValues, string>;
    export let field: Field<TValues, TValue>;
    export let toText: (value: TValue) => string | null | undefined;
    export let fromText: (string: string) => TValue;
    export let autoFocus: boolean = false;
    export let placeholder = '';
    let className: string = '';
    export { className as class };

    const intl = getContainer().intl();

    type ResultType = DocumentType<typeof MentionsQueryDocument>['results'];

    let participants: ResultType = [];

    let editableElement: HTMLElement;
    let menuElement: HTMLElement;

    let clientWidth = 0;

    let v = form.stores().fieldValue(field);

    let innerHtml = toInnerHTML(toText($v) ?? '');

    let runningInFirefox = false;

    $: nbSubmits = form.stores().nbSubmits();
    $: isAlreadyBlur = form.stores().isAlreadyBlur(field);
    $: hasAnyErrors = form.stores().hasFieldAnyErrors(field);
    $: id = form.getFieldId(field);
    $: updateFieldIfNeeded(innerHtml);
    $: updateInnerHtmlIfNeeded($v);

    function updateFieldIfNeeded(value: string) {
        let val = getValueText(value, editableElement);
        let newValue = fromText(val);
        if ($v != newValue) form.dispatch(form.actions().changeValue(newValue, field));
    }

    function updateInnerHtmlIfNeeded(newValue: TValue) {
        let value = getValueText(innerHtml, editableElement);
        if (value != toText(newValue)) {
            innerHtml = toInnerHTML(toText(newValue) ?? '');
        }
    }

    const apollo = getContainer().apollo();

    const mentionData: {
        anchorNode: Node | ChildNode | undefined;
        anchorOffset: number | undefined;
    } = {
        anchorNode: undefined,
        anchorOffset: undefined,
    };

    let caretSelection: Selection | null | undefined = null;

    function getId(participant: { id: number; __typename: string }) {
        return `${participant.__typename}-${participant.id}`;
    }

    function onSelect(id: string) {
        let selected = find(participants, (p) => getId(p) === id);
        participants = [];
        open = false;

        if (
            mentionData.anchorNode != null &&
            mentionData.anchorOffset != null &&
            selected != null
        ) {
            let beforeText = mentionData.anchorNode?.textContent?.substring(
                0,
                mentionData.anchorOffset,
            );
            let afterText = mentionData.anchorNode?.textContent?.substring(
                mentionData.anchorOffset,
            );

            //let matches = beforeText?.match(/(.*(?<=\s|^))(@[A-zÀ-ú0-9]+)$/); //safari problem
            let matches = beforeText?.match(/(.*(?:\s|^))(@[A-zÀ-ú0-9]+)$/);
            if (matches != null) {
                let anchorNode = mentionData.anchorNode;
                let span = document.createElement('span');
                span.contentEditable = 'false';
                span.setAttribute('data-id', getId(selected));
                span.setAttribute('data-label', selected.participantName);
                span.className =
                    'text-white px-1 mx-1 cursor-pointer inline-block bg-blue-500 rounded';
                span.innerText = selected.participantName;

                let parent = anchorNode.parentNode;
                let beforeNode: Node | null = null;
                let afterNode: Node | null = null;

                let space = document.createElement('span');
                space.innerHTML = '&nbsp;';

                if (matches[1] != null && matches[1] != '') {
                    beforeNode = document.createTextNode(matches[1]);
                    parent?.insertBefore(beforeNode, anchorNode);
                }
                if (afterText != '' && afterText != null) {
                    afterNode = document.createTextNode(afterText);
                    parent?.insertBefore(afterNode, anchorNode);
                }
                if (beforeNode != null) {
                    parent?.insertBefore(span, beforeNode.nextSibling);
                    parent?.removeChild(anchorNode);
                } else if (afterNode != null) {
                    parent?.insertBefore(span, afterNode);
                    parent?.removeChild(anchorNode);
                } else {
                    parent?.replaceChild(span, anchorNode);
                }

                parent?.insertBefore(space, span.nextSibling);

                updateFieldIfNeeded(innerHtml);

                editableElement.focus();
                tick().then(() => {
                    caretSelection?.setPosition(space, space.textContent?.length);
                });
            }
        }

        return false;
    }

    let open = false;

    function onOpenChange(value: boolean) {
        if (!value && (participants?.length ?? 0) > 0) {
            participants = [];
            tick().then(() => {
                editableElement.focus();
                setTimeout(() => {
                    let s = document.getSelection();
                    if (mentionData.anchorNode != null && s != null) {
                        s.setPosition(mentionData.anchorNode, mentionData.anchorOffset);
                    }
                }, 100);
            });
        }
    }

    $: onOpenChange(open);

    onMount(() => {
        if (autoFocus) {
            updateFieldIfNeeded(innerHtml);
            tick().then(() => setCursorToEnd());
        }
        runningInFirefox = isFirefox();
    });

    function checkMention(_text: string) {
        if (typeof document === 'undefined') return;

        let s = document.getSelection();
        let anchorNode = s?.anchorNode;

        if (
            s?.isCollapsed &&
            anchorNode != null &&
            editableElement?.contains(anchorNode)
        ) {
            let before = anchorNode.textContent?.substring(0, s?.anchorOffset);
            //let matches = before?.match(/.*(?<=\s|^)(@[A-zÀ-ú0-9]+)$/); //safari problem
            let matches = before?.match(/(.*?)(?:\s|^)(@[A-zÀ-ú0-9]+)$/);
            if (matches != null) {
                let name = matches[2];
                let _name = name?.replace('@', '');

                if (_name != null && _name.length >= 2) {
                    mentionData.anchorNode = anchorNode;
                    mentionData.anchorOffset = s.anchorOffset;
                    fetchParticipants(_name, (list) => {
                        participants = list;
                        open = (list?.length ?? 0) > 0;
                    });
                }
            } else {
                if (open) open = false;
            }
        }
    }

    function onBlur() {
        form.dispatch(form.actions().blur(field));
    }

    function onFocus() {
        form.dispatch(form.actions().focus(field));
    }

    const fetchParticipants = debounce(
        async (query: string, callback: (participants: ResultType) => void) => {
            let list =
                (
                    await apollo.query({
                        query: MentionsQueryDocument,
                        variables: { q: query },
                    })
                ).data?.results ?? [];

            callback(list);
        },
        1000,
        { trailing: true },
    );

    function getValueText(_text: string, element: HTMLElement | ChildNode) {
        if (element == null) return '';
        let result = '';
        let children = element.childNodes;
        for (let child of children) {
            let dataId = (child as HTMLElement).getAttribute?.('data-id');
            if (child.nodeName == 'DIV') {
                result += '\n' + getValueText(child.textContent || '', child);
            } else if (child.nodeName == 'BR') {
                result += `\n`;
            } else if (dataId != null) {
                result += `@[${(child as HTMLElement).getAttribute?.('data-label')}](${dataId})`;
            } else if (child.nodeName == 'SPAN') {
                result += getValueText(child.textContent || '', child);
            } else {
                result += `${child.textContent}`;
            }
        }
        return result;
    }

    $: checkMention(innerHtml);

    function trackCaret() {
        if (!open) {
            let s = document.getSelection();
            if (s == null || !editableElement.contains(s.anchorNode)) {
                caretSelection = null;
            } else {
                caretSelection = s;
            }
        }
    }

    function onKeyDown(e: KeyboardEvent) {
        if (e.key.match(/ArrowUp|ArrowDown|Enter/)) {
            if (open) {
                if (e.key === 'ArrowUp') {
                    focusedIndex =
                        focusedIndex === null || focusedIndex === 0
                            ? null
                            : focusedIndex - 1;
                    if (focusedIndex !== null) {
                        let target = menuElement.children[focusedIndex];
                        target?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                        });
                        e.preventDefault();
                        e.stopPropagation();
                    }
                } else if (e.key === 'ArrowDown') {
                    focusedIndex = focusedIndex === null ? 0 : focusedIndex + 1;
                    if (focusedIndex === menuElement.childElementCount) {
                        focusedIndex = menuElement.childElementCount - 1;
                    }
                    if (focusedIndex !== null) {
                        let target = menuElement.children[focusedIndex];
                        target?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                        });
                        e.preventDefault();
                        e.stopPropagation();
                    }
                } else if (e.key === 'Enter' && focusedIndex !== null) {
                    let participant = participants?.[focusedIndex];
                    if (participant != null) onSelect(getId(participant));
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        }
    }

    function onKeyUp(_e: KeyboardEvent) {
        trackCaret();
    }

    function toInnerHTML(text: string) {
        const REGEX_MENTION_GLOBAL = /@\[[A-zÀ-ú0-9\s,'\-+./()&]+]\(\w+-\d+\)/g;
        const REGEX_MENTION = /@\[(.+)]\((.+)\)/;

        let result = text.replace(/\n/g, '<br/>');

        result = result.replace(REGEX_MENTION_GLOBAL, function (token) {
            let matches = token.match(REGEX_MENTION);
            if (matches) {
                let [, name, id] = matches;
                return `<span contentEditable="false" data-id="${id}" data-label="${name}" class="text-white px-1 cursor-pointer inline-block bg-blue-500 rounded">@${name}</span>`;
            }
            return token;
        });

        result = result.replace(/\s$/, '&nbsp;');

        return result;
    }

    function setCursorToEnd() {
        editableElement.focus();
        // Move the cursor to the end of the content
        let range = document.createRange();
        range.selectNodeContents(editableElement);
        range.collapse(false); // set the cursor to the end
        let selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
    }

    export function insertEmoji(emoji: string) {
        //Do not delete this function, it is used in FeedPostForm even if the IDE says it is not used
        const append = () => {
            editableElement.focus();
            let node = document.createTextNode(emoji);
            editableElement.appendChild(node);
            tick().then(() => {
                caretSelection?.setPosition(node, node.textContent?.length);
            });
            updateFieldIfNeeded(innerHtml);
        };
        if (!editableElement.hasChildNodes()) {
            append();
            return;
        }
        let parent = caretSelection?.anchorNode?.parentNode;
        let anchorNode = caretSelection?.anchorNode;
        let anchorOffset = caretSelection?.anchorOffset;
        if (anchorNode == null || parent == null || anchorOffset == null) return;
        if (!editableElement.contains(anchorNode)) {
            //the input was not focued, append the emoji to the end
            append();
            return;
        }
        let node = document.createTextNode(emoji);
        let beforeText = anchorNode?.textContent?.substring(0, anchorOffset);
        let afterText = anchorNode?.textContent?.substring(anchorOffset);
        let beforeNode: Node | null = null;
        let afterNode: Node | null = null;
        if (beforeText != null && beforeText != '') {
            beforeNode = document.createTextNode(beforeText);
            parent?.insertBefore(beforeNode, anchorNode);
        }
        if (afterText != '' && afterText != null) {
            afterNode = document.createTextNode(afterText);
            parent?.insertBefore(afterNode, anchorNode);
        }
        if (beforeNode != null) {
            parent?.insertBefore(node, beforeNode.nextSibling);
            parent?.removeChild(anchorNode);
        } else if (afterNode != null) {
            parent?.insertBefore(node, afterNode);
            parent?.removeChild(anchorNode);
        } else {
            parent?.replaceChild(node, anchorNode);
        }
        updateFieldIfNeeded(innerHtml);
        tick().then(() => {
            caretSelection?.setPosition(node, node.textContent?.length);
        });
    }

    function isFirefox() {
        return (
            typeof navigator !== 'undefined' &&
            navigator.userAgent.toLowerCase().includes('firefox')
        );
    }

    let focusedIndex: number | null = null;
    $: if (innerHtml) focusedIndex = null;
</script>

{#if runningInFirefox === false}
    <div
        bind:this={editableElement}
        bind:clientWidth
        style:max-width={clientWidth > 0 ? `${clientWidth}px` : 'auto'}
        {id}
        tabindex="0"
        role="textbox"
        data-placeholder={placeholder}
        contenteditable="plaintext-only"
        bind:innerHTML={innerHtml}
        on:click={() => trackCaret()}
        class={cx(
            'ls:min-h-[60px] control-form',
            ($nbSubmits > 0 || $isAlreadyBlur) && $hasAnyErrors
                ? 'control-form--invalid'
                : '',
            className,
        )}
        on:keyup={onKeyUp}
        on:keydown={onKeyDown}
        on:focus={onFocus}
        on:blur={onBlur}
    ></div>
{:else}
    <!-- Content-editable must not be dynamic if using two way binding -->
    <div
        on:paste={(e) => {
            let text = e.clipboardData?.getData('text/plain');
            let html = e.clipboardData?.getData('text/html');

            if (text !== html && text != null && html != null) e.preventDefault();
            else {
                return;
            }

            if (text == null) return;

            const selection = window.getSelection();
            if (!selection?.rangeCount) return;
            selection.deleteFromDocument();
            selection.getRangeAt(0).insertNode(document.createTextNode(text));
            selection.collapseToEnd();
            updateFieldIfNeeded(innerHtml);
        }}
        bind:this={editableElement}
        bind:clientWidth
        style:max-width={clientWidth > 0 ? `${clientWidth}px` : 'auto'}
        {id}
        tabindex="0"
        role="textbox"
        data-placeholder={placeholder}
        contenteditable="true"
        bind:innerHTML={innerHtml}
        on:click={() => trackCaret()}
        class={cx(
            'ls:min-h-[60px] control-form',
            ($nbSubmits > 0 || $isAlreadyBlur) && $hasAnyErrors
                ? 'control-form--invalid'
                : '',
            className,
        )}
        on:keyup={onKeyUp}
        on:keydown={onKeyDown}
        on:focus={onFocus}
        on:blur={onBlur}
    ></div>
{/if}

<div class="relative">
    {#if open}
        <div
            use:clickOutside
            on:outclick={() => {
                open = false;
            }}
            class="absolute mt-2 w-full p-px bg-white border border-solid border-black/20 rounded shadow-xl z-1 max-h-[300px] overflow-auto"
        >
            <ul class="mx-0 my-2 p-0 list-none" bind:this={menuElement}>
                {#each participants ?? [] as participant, index (participant)}
                    <div
                        class="flex gap-4 p-2 hover:bg-blue-100"
                        class:bg-blue-100={focusedIndex === index}
                        on:click={() => onSelect(getId(participant))}
                        on:keypress={(e) => {
                            if (e.key === 'Enter') onSelect(getId(participant));
                        }}
                        role="button"
                        tabindex="0"
                    >
                        <ParticipantAvatar
                            participant={useFragment(
                                ParticipantAvatarFragmentDoc,
                                participant,
                            )}
                            size={'sm'}
                            dontWrapInLink
                        />

                        <div class={'grow'}>
                            <div class={'mb-2'}>{participant.participantName}</div>

                            {#if participant.__typename === 'Company' && participant.category}
                                <div
                                    class={'text-sm w-full line-clamp-1 text-gray-600 break-all'}
                                >
                                    {participant.category.category}
                                </div>
                            {/if}

                            <div class={'flex items-center'}>
                                {#if participant.participantCountry}
                                    <CountryPicture
                                        class={'mr-2'}
                                        size="24"
                                        country={participant.participantCountry}
                                    />
                                {/if}

                                {#if participant.participantCity || participant.participantCountry}
                                    <span class={'text-sm text-gray-600'}>
                                        {intl.countryLabel(
                                            participant.participantCountry ?? '',
                                        )}
                                        {participant.participantCity
                                            ? ` / ${participant.participantCity}`
                                            : ''}
                                    </span>
                                {/if}
                            </div>
                        </div>
                    </div>
                {/each}
            </ul>
        </div>
    {/if}
</div>

<!--<div class="text-blue-500">{$v}</div>
<div class="my-8"><MentionText text={toText($v)} /></div>
<div class="text-yellow-500">{innerHtml}</div>-->

<style>
    [contenteditable]:empty:not(:focus):before {
        content: attr(data-placeholder);
        color: #a0aec0; /*text-gray-500*/
        pointer-events: none;
        display: block;
    }
</style>
